import { FilterIdsType } from '@app/components/common/FilterSelect/FilterSelect';
import { ICatchesFilterOptions } from '@app/interfaces/catch';
import { tableDataInit } from '@app/pages/FishingOperations/Catches/constants';
import { formatFromFilter, formatToFilter } from '@app/utils/formatDate';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Dayjs } from 'dayjs';

export interface CatchesFiltersState {
  filterOptions: ICatchesFilterOptions;
  shouldTriggerFetch: boolean;
  selectedWaterbodyIds: FilterIdsType;
  selectedFishtypeIds: FilterIdsType;
  selectedStatuses: string[];
  fromFilterTemp: Dayjs | null | undefined;
  toFilterTemp: Dayjs | null | undefined;
  fromSizeTemp: number | undefined;
  toSizeTemp: number | undefined;
  fromSize: number | undefined;
  toSize: number | undefined;
  selectedWaterbodyIdsTemp: FilterIdsType;
  selectedFishtypeIdsTemp: FilterIdsType;
  selectedStatusesTemp: string[];
  multiSearch: string;
}

const initialState: CatchesFiltersState = {
  filterOptions: {
    uniqueWaterbodies: [],
    uniqueFishTypes: [],
    uniqueStatuses: tableDataInit.filterOptions.uniqueStatuses,
    uniqueUsers: [],
  },
  shouldTriggerFetch: false,
  selectedWaterbodyIds: [],
  selectedFishtypeIds: [],
  selectedStatuses: [],
  fromFilterTemp: undefined,
  toFilterTemp: undefined,
  fromSizeTemp: undefined,
  toSizeTemp: undefined,
  fromSize: undefined,
  toSize: undefined,
  selectedWaterbodyIdsTemp: [],
  selectedFishtypeIdsTemp: [],
  selectedStatusesTemp: [],
  multiSearch: '',
};

const catchesFiltersSlice = createSlice({
  name: 'catchesFilters',
  initialState,
  reducers: {
    setFilterOptions(state, action: PayloadAction<ICatchesFilterOptions>) {
      state.filterOptions = action.payload;
    },
    setShouldTriggerFetch(state, action: PayloadAction<boolean>) {
      state.shouldTriggerFetch = action.payload;
    },

    setSelectedWaterbodyIds(state, action: PayloadAction<FilterIdsType>) {
      state.selectedWaterbodyIds = action.payload;
    },
    setSelectedFishTypesIds(state, action: PayloadAction<FilterIdsType>) {
      state.selectedFishtypeIds = action.payload;
    },
    setSelectedStatuses(state, action: PayloadAction<string[]>) {
      state.selectedStatuses = action.payload;
    },

    setFromSize(state, action: PayloadAction<number>) {
      state.fromSize = action.payload;
    },
    setToSize(state, action: PayloadAction<number>) {
      state.toSize = action.payload;
    },

    setFromFilterTemp(state, action: PayloadAction<Dayjs | null | undefined>) {
      state.fromFilterTemp = action.payload;
      state.shouldTriggerFetch = true;
    },
    setToFilterTemp(state, action: PayloadAction<Dayjs | null | undefined>) {
      state.toFilterTemp = action.payload;
      state.shouldTriggerFetch = true;
    },

    setFromSizeFilterTemp(state, action: PayloadAction<number | undefined>) {
      state.fromSizeTemp = action.payload;
    },
    setToSizeFilterTemp(state, action: PayloadAction<number | undefined>) {
      state.toSizeTemp = action.payload;
    },

    setSelectedWaterbodyIdsTemp(state, action: PayloadAction<FilterIdsType>) {
      state.selectedWaterbodyIdsTemp = action.payload;
    },
    setSelectedFishTypesIdsTemp(state, action: PayloadAction<FilterIdsType>) {
      state.selectedFishtypeIdsTemp = action.payload;
    },

    setSelectedStatusesTemp(state, action: PayloadAction<string[]>) {
      state.selectedStatusesTemp = action.payload;
    },

    setMultiSearch(state, action: PayloadAction<string>) {
      state.multiSearch = action.payload;
    },

    resetFilters(state) {
      state.selectedWaterbodyIds = initialState.selectedWaterbodyIds;
      state.selectedFishtypeIds = initialState.selectedFishtypeIds;
      state.selectedStatuses = initialState.selectedStatuses;
      state.fromFilterTemp = initialState.fromFilterTemp;
      state.toFilterTemp = initialState.toFilterTemp;
      state.fromSize = initialState.fromSize;
      state.toSize = initialState.toSize;
      state.selectedWaterbodyIdsTemp = initialState.selectedWaterbodyIdsTemp;
      state.selectedFishtypeIdsTemp = initialState.selectedFishtypeIdsTemp;
      state.selectedStatusesTemp = initialState.selectedStatusesTemp;
      state.shouldTriggerFetch = true;
      state.fromSizeTemp = initialState.fromSizeTemp;
      state.toSizeTemp = initialState.toSizeTemp;
      state.multiSearch = initialState.multiSearch;
    },
  },
});

export const fetchParamsSelector = (state: { catchesFilters: CatchesFiltersState }) => {
  const {
    selectedWaterbodyIds,
    selectedFishtypeIds,
    selectedStatuses,
    fromFilterTemp,
    toFilterTemp,
    fromSize,
    toSize,
    multiSearch,
  } = state.catchesFilters;

  const fromFilterString = fromFilterTemp ? formatFromFilter(fromFilterTemp) : '';
  const toFilterString = toFilterTemp ? formatToFilter(toFilterTemp) : '';

  return {
    selectedWaterbodyIds,
    selectedFishtypeIds,
    selectedStatuses,
    fromFilterString,
    toFilterString,
    fromSize,
    toSize,
    multiSearch,
  };
};

export const areFiltersAppliedSelector = (state: { catchesFilters: CatchesFiltersState }) => {
  const {
    selectedWaterbodyIds,
    selectedFishtypeIds,
    selectedStatuses,
    fromFilterTemp,
    toFilterTemp,
    fromSize,
    toSize,
    multiSearch,
  } = state.catchesFilters;

  return (
    !!selectedWaterbodyIds.length ||
    !!selectedFishtypeIds.length ||
    !!selectedStatuses.length ||
    !!fromFilterTemp ||
    !!toFilterTemp ||
    !!fromSize ||
    !!toSize ||
    !!multiSearch
  );
};

export const activeFiltersCountSelector = (state: { catchesFilters: CatchesFiltersState }) => {
  const {
    selectedWaterbodyIds,
    selectedFishtypeIds,
    selectedStatuses,
    fromFilterTemp,
    toFilterTemp,
    fromSize,
    toSize,
    multiSearch,
  } = state.catchesFilters;

  let count = 0;
  if (selectedWaterbodyIds.length > 0) count++;
  if (selectedFishtypeIds.length > 0) count++;
  if (selectedStatuses.length > 0) count++;
  if (fromFilterTemp) count++;
  if (toFilterTemp) count++;
  if (fromSize) count++;
  if (toSize) count++;
  if (multiSearch) count++;

  return count;
};

export const {
  setSelectedWaterbodyIds,
  setSelectedStatuses,
  setSelectedFishTypesIds,
  setFromFilterTemp,
  setToFilterTemp,
  setFromSizeFilterTemp,
  setToSizeFilterTemp,
  setFromSize,
  setToSize,
  setSelectedFishTypesIdsTemp,
  setSelectedWaterbodyIdsTemp,
  setSelectedStatusesTemp,
  setMultiSearch,
  setShouldTriggerFetch,
} = catchesFiltersSlice.actions;

export const catchesFiltersActions = catchesFiltersSlice.actions;

export const catchesFilters = catchesFiltersSlice.reducer;
